import { useState, useEffect, useMemo } from 'react';

import Icon from 'react-web-vector-icons';

import PopupWin from './PopupWin';
import PopupButton from './PopupButton';
import Toaster from './Toaster';

import DragVerticalIcon from '../assets/drag-vertical.svg';

import '../styles/list-edit-modal.scss';
import '../styles/column-selector-modal.scss';

const ListEditModal = ({ mode, initial, availableFields, onClose, onSave, error, loading }) => {
  const getValidFields = (raw, availableFields, fallback = []) => {
    try {
      const parsed = JSON.parse(raw || '[]');
      const availableKeys = new Set(availableFields.map(f => f.key));

      const filtered = Array.isArray(parsed)
        ? parsed.filter(key => availableKeys.has(key))
        : [];
      console.log('filtered',filtered);

      return filtered.length ? filtered : fallback;
    } catch (e) {
      console.error('Error parsing fields_json:', e);
      return fallback;
    }
  };

  const initialValidFields = getValidFields(initial?.fields_json, availableFields);

  const [name, setName] = useState(initial?.list_name || '');
  const [visibility, setVisibility] = useState(initial?.visibility || 'private');

  const validName = /^[a-zA-Z0-9 ]+$/.test(name);  

  const [selected, setSelected] = useState(initialValidFields);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedVisible, setSelectedVisible] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const available = availableFields.filter(f => !selected.includes(f.key));
  const canSave = name && validName && selected.length > 0;


  
  const getTitle = () => {
    switch (mode) {
      case 'edit': return 'Edit List';
      default: return 'Create New List';
    }
  }

  const moveToVisible = () => {
    if (selectedAvailable && !selected.includes(selectedAvailable)) {
      setSelected([...selected, ...selectedAvailable]);
      setSelectedAvailable([]);
    }
  };

  const removeField = (key) => {
    setSelected(selected.filter(f => f !== key));
    setSelectedVisible(null);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add a custom drag image
    const dragImage = e.target.cloneNode(true);
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === targetIndex) return;

    const newSelected = [...selected];
    const [removed] = newSelected.splice(draggedItem, 1);
    newSelected.splice(targetIndex, 0, removed);
    setSelected(newSelected);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const toSnakeCase = (str) => {
    if (typeof str !== 'string') return str;
    return str
      // 1. put underscore between lower-to-upper transitions: "fooBar" -> "foo_Bar"
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      // 2. replace non-alphanum (including spaces, dashes) with underscore
      .replace(/[^A-Za-z0-9]+/g, '_')
      // 3. collapse multiple underscores
      .replace(/_+/g, '_')
      // 4. remove leading/trailing underscores
      .replace(/^_|_$/g, '')
      // 5. lowercase
      .toLowerCase();
  }

  const uniqueTimestampId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
  };

  const handleSave = () => {
    if (!name) {
      Toaster.showWarn('List Name is required.');
      return;
    }

    if (!validName) {
      Toaster.showWarn('List Name can only contain English letters, numbers, and spaces.');
      return;
    }

    const key = toSnakeCase(name) + '_' + uniqueTimestampId();
    
    onSave({ list_name: name, list_key: key, visibility }, selected);
  };

  const footer = useMemo(() => (
    <>
      <PopupButton
        variant="secondary"
        onClick={onClose}
        disabled={loading}
      >
        Cancel
      </PopupButton>

      <PopupButton
        variant="primary"
        onClick={handleSave}
        disabled={loading || !canSave}
        loading={loading}
      >
        {mode === 'edit' ? 'Apply Changes' : 'Create List'}
      </PopupButton>
    </>
  ), [mode, onClose, handleSave, loading, canSave]);

  useEffect(() => {
    try {
      const allFields = JSON.parse(initial?.fields_json || '{}');
      setSelected(initialValidFields);
    } catch (e) {
      console.error('Error parsing fields_json:', e);
      setSelected(initialValidFields);
    }
  }, [initial?.fields_json]);

  return (
    <PopupWin
      visible={true}
      onClose={onClose}
      title={getTitle()}
      popupStyle={{ minWidth: '700px'}}
      footer={footer}
    >
      <div className="list-edit-modal-wrapper">
        <div className="list-edit-modal-content">
          <div className="form-group">
            <label className="form-label required">List Name</label>
            <input
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter list name (English letters, numbers, spaces)"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Visibility</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  className="radio-primary"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                  disabled={loading}
                />
                <span>Private (Only you can see this list)</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  className="radio-primary"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                  disabled={loading}
                />
                <span>Shared (Everyone in your team can see this list)</span>
              </label>
            </div>
          </div>

        <div className="form-group">
          <label className="form-label required">Fields to Display</label>
        </div>
    
        <div className="column-selector-modal-wrapper">
          <div className="column-selector-modal-content">
            <div className="field-columns">
            {/* Available Fields */}
            <div className="field-column">
              <div className="field-list">
                {available.map(f => (
                  <div
                    key={f.key}
                    className="field-item available"
                    onClick={() => {
                      setSelectedAvailable((prev) => {
                        if (prev.includes(f.key)) {
                          return prev.filter(k => k !== f.key);
                        }

                        return [...prev, f.key];
                      });
                      setSelectedVisible(null);
                    }}
                  >
                    <input type="checkbox" className="checkbox-primary" checked={selectedAvailable.includes(f.key)} readOnly />
                    {f.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Arrow Buttons */}
            <div className="arrow-buttons">
              <button
                className="arrow-button"
                onClick={moveToVisible}
                disabled={!selectedAvailable.length}
                title="Add to visible"
              >
                <Icon name="chevron-small-right" font="Entypo" color="#142A31" size={20} />
              </button>
            </div>

            {/* Visible Fields */}
            <div className="field-column">
              <div className="field-list">
                {selected.map((key, idx) => {
                  const field = availableFields.find(f => f.key === key);
                  const isDragging = draggedItem === idx;
                  const isDragOver = dragOverIndex === idx;

                  return (
                    <div
                      key={key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, idx)}
                      className={`field-item draggable ${selectedVisible === key ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                      onClick={() => {
                        setSelectedVisible(key);
                        setSelectedAvailable([]);
                      }}
                    >
                      <div className="field-item-content">
                        <span className="drag-handle">
                          <img 
                            src={DragVerticalIcon} 
                            alt="Drag" 
                          />
                        </span>
                        <span className="field-label">{field?.label || key}</span>
                      </div>

                      <div className="field-item-actions">
                        <button
                          className="field-action-button remove"
                          title="Remove"
                          onClick={e => { e.stopPropagation(); removeField(key); }}
                        >
                          <Icon 
                            name="close" 
                            font="AntDesign" 
                            color="#D3E1ED" 
                            size={16}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          </div>
        </div>

          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </PopupWin>
    
  )
}

export default ListEditModal; 